import React, { useState, useEffect, useRef } from 'react';
import './DashboardSearch.css';
import { useNavigate, useLocation } from 'react-router-dom';

const mockTags = [
  "nature", "voyage", "musique", "sport", "éducation",
  "documentaire", "culinaire", "technologie", "environnement", "histoire",
  "société", "humour", "art", "cinéma", "science",
  "animaux", "santé", "mode", "culture", "actualité"
];

const FeaturedCard = React.memo(({ board, isActive, onClick }) => (
  <div
    className={`featured-card ${isActive ? 'active' : ''}`}
    onClick={onClick}
  >
    <img src={board.image} alt={board.title} className="featured-image" />
    <div className="featured-card-title">{board.title}</div>
  </div>
));

const DashboardSearch = ({ onSearch, initiallyCollapsed = false }) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(!initiallyCollapsed);
  const [selectedTags, setSelectedTags] = useState([]);
  const [clickedCard, setClickedCard] = useState(null);
  const [featuredBoards, setFeaturedBoards] = useState([]);
  const carouselWrapperRef = useRef(null);
  const carouselTrackRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Fermer si filtre présent
  useEffect(() => {
    setShowSuggestions(false);
    document.body.classList.remove('search-overlay');
  }, [location.pathname, location.search]);

  // Fermer au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
        document.body.classList.remove('search-overlay');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Charger médias
  useEffect(() => {
    fetch('/media/public')
      .then(res => res.json())
      .then(data => {
        const boards = data.slice(0, 20).map(m => ({
          title: m.title || m.description || "Sans titre",
          image: `/uploads/${m.file_path}`,
          tag: m.title?.toLowerCase().split(' ')[0] || "inspiré"
        }));
        setFeaturedBoards(boards);
      })
      .catch(console.error);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    onSearch?.(value);
  };

  const handleFocus = () => {
    setShowSuggestions(true);
    document.body.classList.add('search-overlay');
  };

  const handleTagClick = (tag) => {
    setSelectedTags([tag]);
    setShowSuggestions(false);
    document.body.classList.remove('search-overlay');
    requestAnimationFrame(() => navigate(`/dashboard?filter=${encodeURIComponent(tag)}`));
  };

  const handleRemoveTag = (tag) => {
    const newTags = selectedTags.filter(t => t !== tag);
    setSelectedTags(newTags);
    onSearch?.(newTags.join(' '));
  };

  const scrollCarousel = (direction) => {
    const wrapper = carouselWrapperRef.current;
    if (wrapper) {
      wrapper.scrollBy({
        left: direction === 'left' ? -200 : 200,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div
  className={`search-container full-width px-2 px-sm-3 ${showSuggestions ? 'active-border' : ''}`}
  ref={containerRef}
      style={{
        paddingBottom: showSuggestions ? '32px' : '16px',
        backgroundColor: showSuggestions ? '#f3f4f6' : 'transparent',
        border: showSuggestions ? '1px solid #D1D5DB' : 'none',
        boxShadow: showSuggestions ? '0 4px 12px rgba(0, 0, 0, 0.05)' : 'none',
        transition: 'all 0.3s ease'
      }}
    >
      <div className="search-input-wrapper flex-inside">
        <i className="bi bi-search search-icon"></i>
        <div className="search-box-content">
          {selectedTags.map((tag, idx) => (
            <span key={idx} className="inline-tag">
              {tag}
              <button type="button" onClick={() => handleRemoveTag(tag)}>&times;</button>
            </span>
          ))}
          <input
            type="text"
            className="search-input no-shadow"
            placeholder={selectedTags.length === 0 ? "Rechercher une étape, une émotion, un souvenir de voyage…" : ""}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
          />
        </div>
      </div>

      {showSuggestions && (
        <>
          <div className="tag-suggestions">
            {mockTags.map((tag, idx) => (
              <button key={idx} className="tag-item" onClick={() => handleTagClick(tag)}>
                {tag}
              </button>
            ))}
          </div>

          <div className="featured-boards">
            <h6 className="featured-title">Explorer les suggestions</h6>
            <div className="carousel-container">
              <button className="carousel-btn left" onClick={() => scrollCarousel('left')}>❮</button>
              <div className="carousel-wrapper" ref={carouselWrapperRef}>
                <div className="carousel-track" ref={carouselTrackRef}>
                  {featuredBoards.map((board, idx) => (
                    <FeaturedCard
                      key={idx}
                      board={board}
                      isActive={clickedCard === idx}
                      onClick={() => {
                        setClickedCard(idx);
                        setTimeout(() => {
                          setClickedCard(null);
                          handleTagClick(board.tag);
                        }, 300);
                      }}
                    />
                  ))}
                </div>
              </div>
              <button className="carousel-btn right" onClick={() => scrollCarousel('right')}>❯</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardSearch;
